import { memo, type FC } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { Capsule } from 'Capsule';
import { ContentItemIcon } from 'ContentItemIcon';
import { Text } from 'Text';
import { Popover } from 'Popover';
import { parseHtml } from 'FrontRoyalAngular/html.utils';
import { originalUrl } from 'hasResizableImages';
import comingSoonIcon from 'images/coming_soon_icon.png';
import trackIconLocked from 'vectors/track_icon_locked_slate_grey.svg';
import { type BrowseStream, type ProgressStatus } from '../BrowseCourses.types';
import { NEUTRAL_CAPSULE_CLASS_NAMES, SELECTED_CAPSULE_CLASS_NAMES } from '../constants';
import '../locales/browseCourses/browseCourses-en.json';

type Props = {
    stream: BrowseStream;
    topics: string[];
    progressStatus: ProgressStatus;
    lessonsCompletedCount: number;
    locked: boolean;
    // The access reason of a locked course on desktop. It can hold links, so it renders as HTML.
    lockedMessage: string | null;
    onPress: (stream: BrowseStream) => void;
};

// Decorative: the title sits next to the image, so an alt text would repeat it in the button's name
const CourseImage: FC<{ stream: BrowseStream; locked: boolean }> = ({ stream, locked }) => {
    if (stream.coming_soon) return <img src={comingSoonIcon} alt="" className="h-12 w-12 shrink-0" />;
    if (locked) return <img src={trackIconLocked} alt="" className="h-12 w-12 shrink-0" />;
    return <ContentItemIcon src={originalUrl(stream.image) ?? ''} alt="" size={12} cacheForever />;
};

const CompletionLine: FC<{ stream: BrowseStream; progressStatus: ProgressStatus; completed: number }> = ({
    stream,
    progressStatus,
    completed,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'browseCourses.browseCourses' });
    if (progressStatus === 'not_started' || stream.lessons.length === 0) return null;

    const isComplete = progressStatus === 'completed';
    return (
        <Text as="div" variant="small" color={isComplete ? 'primary' : 'purple'} weight="semibold">
            {isComplete && <FontAwesomeIcon icon={faCheck} className="me-1" />}
            {t('lessonCompletionCount', { completed, count: stream.lessons.length })}
        </Text>
    );
};

// A course in the Courses page results. The card styling matches ContentItemBox, which has no room for
// a description, topics, or status markers. Like the Angular card, the topics are hidden on phones.
export const BrowseCourseCard: FC<Props> = memo(
    ({ stream, topics, progressStatus, lessonsCompletedCount, locked, lockedMessage, onPress }) => {
        const { t } = useTranslation('back_royal', { keyPrefix: 'browseCourses.browseCourses' });
        const showNewOrUpdated = (stream.just_added || stream.just_updated) && !stream.coming_soon;
        const showBeta = stream.beta && !stream.coming_soon;
        const hasMarkers = showNewOrUpdated || showBeta || !!stream.coming_soon;

        const card = (
            <button
                type="button"
                data-id={stream.id}
                onClick={lockedMessage ? undefined : () => onPress(stream)}
                className="flex h-full w-full flex-col gap-2.5 rounded-5 border border-solid border-slate-grey-200 bg-white p-3.5 text-start transition-all duration-200 hover:border-slate-grey-300 hover:shadow-small"
            >
                <div className="flex w-full items-center gap-2.5">
                    <CourseImage stream={stream} locked={locked} />
                    <Text
                        as="div"
                        variant="h3"
                        color={locked ? 'slate-grey' : 'black'}
                        className="line-clamp-2 min-w-0 flex-1 text-md leading-6"
                    >
                        {stream.title}
                    </Text>
                </div>

                {stream.description && (
                    <Text as="div" variant="small" className="line-clamp-2">
                        {stream.description}
                    </Text>
                )}

                <CompletionLine stream={stream} progressStatus={progressStatus} completed={lessonsCompletedCount} />

                {(hasMarkers || topics.length > 0) && (
                    <div className={clsx('mt-auto flex flex-wrap gap-1.5', !hasMarkers && 'max-sm:hidden')}>
                        {showNewOrUpdated && (
                            <Capsule size="sm" classNames={SELECTED_CAPSULE_CLASS_NAMES}>
                                {stream.just_added ? t('newBadge') : t('updatedBadge')}
                            </Capsule>
                        )}
                        {showBeta && (
                            <Capsule size="sm" classNames={SELECTED_CAPSULE_CLASS_NAMES}>
                                {t('betaBadge')}
                            </Capsule>
                        )}
                        {stream.coming_soon && (
                            <Capsule size="sm" classNames={SELECTED_CAPSULE_CLASS_NAMES}>
                                {t('comingSoonBadge')}
                            </Capsule>
                        )}
                        <div className="contents max-sm:hidden">
                            {[...topics].sort().map(topic => (
                                <Capsule key={topic} size="sm" classNames={NEUTRAL_CAPSULE_CLASS_NAMES}>
                                    {topic}
                                </Capsule>
                            ))}
                        </div>
                    </div>
                )}
            </button>
        );

        if (!lockedMessage) return card;

        // Like the Angular card, desktop shows the locked reason in a popover that a click opens and
        // an outside click closes
        return (
            <Popover
                placement="top-start"
                borderColor="slate-grey-200"
                classNames={{ content: 'max-w-xs p-2.5 text-center text-xs text-black' }}
                Content={() => <>{parseHtml(lockedMessage)}</>}
            >
                {card}
            </Popover>
        );
    },
);
